"use client"

import { useState, useEffect } from "react"
import "./UserManager.css"
import userService from "../../../services/api/userService"

const UserManager = () => {
    const [users, setUsers] = useState([])
    const [filteredUsers, setFilteredUsers] = useState([])
    const [searchTerm, setSearchTerm] = useState("")
    const [showForm, setShowForm] = useState(false)
    const [editingUserId, setEditingUserId] = useState(null)
    const [formError, setFormError] = useState("")
    const [showPassword, setShowPassword] = useState({})
    const [loading, setLoading] = useState(false)

    const [newUser, setNewUser] = useState({
        username: "",
        password: "",
        confirmPassword: "",
    })

    // Fetch users on component mount
    useEffect(() => {
        fetchUsers()
    }, [])

    // Fetch all users
    const fetchUsers = async () => {
        try {
            setLoading(true)
            const response = await userService.getAllUsers()
            setUsers(response)
            setFilteredUsers(response)
        } catch (error) {
            console.error("Error fetching users:", error)
            setFormError("Không thể tải danh sách người dùng")
        } finally {
            setLoading(false)
        }
    }

    // Filter users based on search term
    useEffect(() => {
        if (searchTerm) {
            const filtered = users.filter((user) => user.username.toLowerCase().includes(searchTerm.toLowerCase()))
            setFilteredUsers(filtered)
        } else {
            setFilteredUsers(users)
        }
    }, [searchTerm, users])

    // Handle input change for new user form
    const handleInputChange = (e) => {
        const { name, value } = e.target
        setNewUser({
            ...newUser,
            [name]: value,
        })

        // Clear error when user types
        if (formError) setFormError("")
    }

    // Validate form
    const validateForm = () => {
        if (!newUser.username.trim()) {
            setFormError("Tên người dùng không được để trống")
            return false
        }

        // Chỉ kiểm tra độ dài mật khẩu khi tạo mới
        if (!editingUserId && newUser.password.length < 6) {
            setFormError("Mật khẩu phải có ít nhất 6 ký tự")
            return false
        }

        if (newUser.password !== newUser.confirmPassword) {
            setFormError("Mật khẩu xác nhận không khớp")
            return false
        }

        return true
    }

    // Add or update user
    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateForm()) return

        try {
            setLoading(true)
            if (editingUserId) {
                // Update existing user
                await userService.updateUser(editingUserId, {
                    username: newUser.username,
                    password: newUser.password
                })
            } else {
                // Add new user
                await userService.createUser({
                    username: newUser.username,
                    password: newUser.password
                })
            }

            // Refresh user list
            await fetchUsers()
            resetForm()
        } catch (error) {
            console.error("Error saving user:", error)
            setFormError("Không thể lưu thông tin người dùng")
        } finally {
            setLoading(false)
        }
    }

    // Reset form and state
    const resetForm = () => {
        setNewUser({
            username: "",
            password: "",
            confirmPassword: "",
        })
        setEditingUserId(null)
        setShowForm(false)
        setFormError("")
    }

    // Start editing a user
    const handleEdit = async (user) => {
        try {
            setLoading(true)
            const userData = await userService.getUserById(user._id)
            setEditingUserId(user._id)
            setNewUser({
                username: userData.username,
                password: "",
                confirmPassword: "",
            })
            setShowForm(true)
            setFormError("")
        } catch (error) {
            console.error("Error fetching user details:", error)
            setFormError("Không thể tải thông tin người dùng")
        } finally {
            setLoading(false)
        }
    }

    // Delete user
    const handleDelete = async (id) => {
        if (window.confirm("Bạn có chắc chắn muốn xóa người dùng này không?")) {
            try {
                setLoading(true)
                await userService.deleteUser(id)
                await fetchUsers()
            } catch (error) {
                console.error("Error deleting user:", error)
                setFormError("Không thể xóa người dùng")
            } finally {
                setLoading(false)
            }
        }
    }

    // Toggle password visibility
    const togglePasswordVisibility = (userId) => {
        setShowPassword({
            ...showPassword,
            [userId]: !showPassword[userId],
        })
    }

    return (
        <div className="user-manager-container">
            <h1>Quản Lý Người Dùng</h1>

            <div className="user-controls">
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Tìm kiếm người dùng..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="search-input"
                    />
                </div>

                <button
                    className="add-button"
                    onClick={() => {
                        setShowForm(!showForm)
                        if (editingUserId) {
                            resetForm()
                        }
                    }}
                >
                    {showForm ? "Hủy" : "Thêm Người Dùng"}
                </button>
            </div>

            {showForm && (
                <div className="user-form-container">
                    <h2>{editingUserId ? "Cập Nhật Người Dùng" : "Thêm Người Dùng Mới"}</h2>
                    {formError && <div className="form-error">{formError}</div>}
                    <form onSubmit={handleSubmit} className="user-form">
                        <div className="form-group">
                            <label htmlFor="username">Tên người dùng:</label>
                            <input
                                type="text"
                                id="username"
                                name="username"
                                value={newUser.username}
                                onChange={handleInputChange}
                                required
                            />
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">
                                {editingUserId ? "Mật khẩu mới (để trống nếu không thay đổi):" : "Mật khẩu:"}
                            </label>
                            <div className="password-input-container">
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    value={newUser.password}
                                    onChange={handleInputChange}
                                    required={!editingUserId}
                                />
                            </div>
                            <div className="password-strength">
                                {newUser.password && (
                                    <div
                                        className={`strength-meter ${newUser.password.length < 6 ? "weak" : newUser.password.length < 10 ? "medium" : "strong"
                                            }`}
                                    >
                                        {newUser.password.length < 6 ? "Yếu" : newUser.password.length < 10 ? "Trung bình" : "Mạnh"}
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="form-group">
                            <label htmlFor="confirmPassword">Xác nhận mật khẩu:</label>
                            <input
                                type="password"
                                id="confirmPassword"
                                name="confirmPassword"
                                value={newUser.confirmPassword}
                                onChange={handleInputChange}
                                required={!editingUserId || newUser.password.length > 0}
                            />
                        </div>

                        <div className="form-actions">
                            <button type="submit" className="submit-button">
                                {editingUserId ? "Cập Nhật" : "Thêm Mới"}
                            </button>
                            <button type="button" className="cancel-button" onClick={resetForm}>
                                Hủy
                            </button>
                        </div>
                    </form>
                </div>
            )}

            <div className="users-table-container">
                <table className="users-table">
                    <thead>
                        <tr>
                            <th>ID</th>
                            <th>Tên người dùng</th>
                            <th>Mật khẩu</th>
                            <th>Thao tác</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.length > 0 ? (
                            filteredUsers.map((user) => (
                                <tr key={user._id}>
                                    <td>{user._id}</td>
                                    <td>{user.username}</td>
                                    <td className="password-cell">
                                        {showPassword[user._id] ? user.actualPassword : user.password}
                                        <button
                                            className="toggle-password-button"
                                            onClick={() => togglePasswordVisibility(user._id)}
                                            title={showPassword[user._id] ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
                                        >
                                            {showPassword[user._id] ? "Ẩn" : "Hiện"}
                                        </button>
                                    </td>
                                    <td className="action-cell">
                                        <button className="edit-button" onClick={() => handleEdit(user)}>
                                            Sửa
                                        </button>
                                        <button className="delete-button" onClick={() => handleDelete(user._id)}>
                                            Xóa
                                        </button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="4" className="no-data">
                                    Không tìm thấy người dùng nào
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    )
}

export default UserManager
